// Cockpit Particulier - Espace personnel pour les vendeurs particuliers
// Inspiré de LeBonCoin avec intégration sociale
// DESKTOP: Interface unifiée style Claude (menu + chat IA + artifacts)
// MOBILE: Layout classique avec bottom navigation

import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, Package, PlusCircle, MessageCircle, Heart, Euro, ShoppingBag,
  Users, User, Settings, ChevronRight, ChevronDown, Bell, TrendingUp, Eye, Clock,
  CheckCircle, AlertTriangle, BarChart3, Wallet, Star, Share2, LogOut,
  Menu, X, Briefcase, Store, Megaphone, Building2, Handshake, Shield,
  FileText, Send, Wrench, Image, BookOpen, Search, Bookmark, UserPlus, Newspaper,
  Calendar, Hash, PenTool, Sparkles, Leaf, HeartHandshake, PartyPopper, PawPrint,
  Plane, Shirt, Laptop, HomeIcon, Palette, Lock, Dumbbell, UtensilsCrossed, Car,
  Sailboat, Pill, Baby, GraduationCap, Brush, Network, Layers, Mail, MoreHorizontal,
  Cloud, ShoppingCart, CreditCard, FolderOpen, Video, Lightbulb, Mic, Copy, PanelRightOpen,
  Plus, ImageIcon, Film, Paperclip, File, Loader2, Crown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import PublishModal from '../../components/publish/PublishModal';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import NotificationBell from '../../components/NotificationBell';
import NotificationsBell from '../../components/notifications/NotificationsBell';
import { AbracaCoinsBadge } from '../../components/credits';
import RegisterPromptModal from '../../components/RegisterPromptModal';
import { API_URL } from '../../config/api';
import { useImmersiveScrollContext } from '../../context/ImmersiveScrollContext';
import ArtifactCard from '../../components/artifacts/ArtifactCard';
import ArtifactViewer from '../../components/artifacts/ArtifactViewer';
import { AbraNotificationContainer } from '../../components/mascot';
import { ThemeToggle } from '../../context/ThemeContext';
import CockpitAssistantUnified from '../cockpit/CockpitAssistantUnified';
import CockpitMainNav from '../../components/layout/CockpitMainNav';

// Configuration AbracadaBati - Sous-catégories avec leurs sous-menus complets
const ABRACADABATI_CATEGORIES = {
  reseau: {
    id: 'reseau',
    label: 'Réseau Social',
    icon: Network,
    subItems: [
      { id: 'r-feed', path: '/cockpit-particulier/reseau', label: 'Mon Feed', icon: Newspaper },
      { id: 'r-notifs', path: '/cockpit-particulier/reseau/notifications', label: 'Notifications', icon: Bell },
      { id: 'r-cercles', path: '/cockpit-particulier/cercles', label: 'Mes équipes', icon: Users, isNew: true },
      { id: 'r-posts', path: '/cockpit-particulier/reseau/posts', label: 'Mes Publications', icon: FileText },
      { id: 'r-blog', path: '/cockpit-particulier/reseau/blog', label: 'Mon Blog', icon: PenTool },
      { id: 'r-galerie', path: '/cockpit-particulier/reseau/galerie', label: 'Ma Galerie', icon: Image },
      { id: 'r-evenements', path: '/cockpit-particulier/reseau/evenements', label: 'Événements', icon: Calendar },
      { id: 'r-salons', path: '/cockpit-particulier/reseau/salons', label: 'Salons', icon: Hash },
      { id: 'r-abonnes', path: '/cockpit-particulier/reseau/abonnes', label: 'Mes Abonnés', icon: Users },
      { id: 'r-abonnements', path: '/cockpit-particulier/reseau/abonnements', label: 'Mes Abonnements', icon: UserPlus },
      { id: 'r-messages', path: '/cockpit-particulier/reseau/messages', label: 'Messages privés', icon: MessageCircle },
    ]
  },
  marketplace: {
    id: 'marketplace',
    label: 'Marketplace',
    icon: Store,
    subItems: [
      { id: 'm-dashboard', path: '/cockpit-particulier', label: 'Tableau de bord', icon: Home },
      { id: 'm-annonces', path: '/cockpit-particulier/annonces', label: 'Mes Annonces', icon: Package },
      { id: 'm-commandes', path: '/cockpit-particulier/marketplace/commandes', label: 'Mes Commandes', icon: ShoppingBag },
      { id: 'm-ventes', path: '/cockpit-particulier/ventes', label: 'Mes Ventes', icon: Euro },
      { id: 'm-achats', path: '/cockpit-particulier/achats', label: 'Mes Achats', icon: ShoppingBag },
      { id: 'm-messages', path: '/cockpit-particulier/messages', label: 'Messages', icon: MessageCircle },
      { id: 'm-favoris', path: '/cockpit-particulier/favoris', label: 'Favoris', icon: Heart },
      { id: 'm-avis', path: '/cockpit-particulier/marketplace/avis', label: 'Mes Avis', icon: Star },
      { id: 'm-vendeurs', path: '/cockpit-particulier/vendeurs-suivis', label: 'Vendeurs suivis', icon: Users },
    ]
  },
  artisans: {
    id: 'artisans',
    label: 'Prestataires de services',
    icon: Handshake,
    subItems: [
      { id: 'a-dashboard', path: '/cockpit-particulier/artisans', label: 'Tableau de bord', icon: Home },
      { id: 'a-devis', path: '/cockpit-particulier/artisans/devis', label: 'Mes Demandes de devis', icon: FileText },
      { id: 'a-chantiers', path: '/cockpit-particulier/artisans/chantiers', label: 'Mes Chantiers', icon: Wrench },
      { id: 'a-favoris', path: '/cockpit-particulier/artisans/favoris', label: 'Artisans favoris', icon: Bookmark },
      { id: 'a-avis', path: '/cockpit-particulier/artisans/avis', label: 'Mes Avis', icon: Star },
    ]
  },
  emploi: {
    id: 'emploi',
    label: 'Emploi',
    icon: Briefcase,
    subItems: [
      { id: 'e-dashboard', path: '/cockpit-particulier/emploi', label: 'Tableau de bord', icon: Home },
      { id: 'e-offres', path: '/cockpit-particulier/emploi/offres', label: 'Offres sauvegardées', icon: Bookmark },
      { id: 'e-candidatures', path: '/cockpit-particulier/emploi/candidatures', label: 'Mes Candidatures', icon: FileText },
      { id: 'e-cv', path: '/cockpit-particulier/emploi/cv', label: 'Mon CV', icon: User },
      { id: 'e-alertes', path: '/cockpit-particulier/emploi/alertes', label: 'Alertes emploi', icon: Bell },
    ]
  },
};

// Liste des 19 univers "Bientôt disponibles" avec emojis et descriptions
const COMING_SOON_UNIVERS = [
  { id: 'home', emoji: '🏠', name: 'Maison', officialName: 'AbracadaHome', description: 'Décoration & Maison' },
  { id: 'green', emoji: '🌿', name: 'Écologie', officialName: 'AbracadaGreen', description: 'Écologie & Développement durable' },
  { id: 'match', emoji: '❤️', name: 'Rencontres', officialName: 'AbracadaMatch', description: 'Rencontres certifiées' },
  { id: 'event', emoji: '🎉', name: 'Événements', officialName: 'AbracadaEvent', description: 'Événementiel & Fêtes' },
  { id: 'zoo', emoji: '🐾', name: 'Animaux', officialName: 'AbracadaZoo', description: 'Animaux & Nature' },
  { id: 'travel', emoji: '✈️', name: 'Voyage', officialName: 'AbracadaTravel', description: 'Voyage & Expériences' },
  { id: 'fashion', emoji: '👗', name: 'Mode', officialName: 'AbracadaFashion', description: 'Mode & Luxe' },
  { id: 'tech', emoji: '💻', name: 'High-Tech', officialName: 'AbracadaTech', description: 'High-Tech & Électronique' },
  { id: 'realty', emoji: '🏡', name: 'Immobilier', officialName: 'AbracadaRealty', description: 'Immobilier Mondial' },
  { id: 'beauty', emoji: '💄', name: 'Beauté', officialName: 'AbracadaBeauty', description: 'Beauté & Cosmétiques' },
  { id: 'secret', emoji: '🔞', name: 'Adultes 18+', officialName: 'AbracadaSecret', description: 'Adultes 18+' },
  { id: 'sport', emoji: '💪', name: 'Sport', officialName: 'AbracadaSport', description: 'Sport & Fitness' },
  { id: 'food', emoji: '🍽️', name: 'Alimentation', officialName: 'AbracadaFood', description: 'Alimentation & Restauration' },
  { id: 'car', emoji: '🚗', name: 'Auto & Moto', officialName: 'AbracadaCar', description: 'Automobile & Moto' },
  { id: 'boat', emoji: '⛵', name: 'Nautisme', officialName: 'AbracadaBoat', description: 'Nautisme & Mer' },
  { id: 'health', emoji: '💊', name: 'Santé', officialName: 'AbracadaHealth', description: 'Santé & Bien-être' },
  { id: 'toys', emoji: '🧸', name: 'Enfants', officialName: 'AbracadaToys', description: 'Jouets & Enfants' },
  { id: 'learn', emoji: '📚', name: 'Formation', officialName: 'AbracadaLearn', description: 'Formation & Coaching' },
  { id: 'art', emoji: '🎨', name: 'Art & Culture', officialName: 'AbracadaArt', description: 'Art & Culture' },
];

// Composant pour une catégorie AbracadaBati avec sous-menu
// Props: category, location, onClose, isExpanded, onToggle, badgeCounts (pour accordéon)
function BatiCategoryItem({ category, location, onClose, isExpanded, onToggle, badgeCounts = {} }) {
  const CategoryIcon = category.icon;
  const hasActiveChild = category.subItems.some(item => 
    location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  );
  
  return (
    <div>
      {/* Header de catégorie - BLANC (pas orange) */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium"
        style={{ 
          color: isExpanded || hasActiveChild ? '#ffffff' : 'rgba(255,255,255,0.85)',
          backgroundColor: isExpanded || hasActiveChild ? 'rgba(255,255,255,0.12)' : 'transparent',
          borderLeft: isExpanded || hasActiveChild ? '2px solid rgba(255,255,255,0.4)' : '2px solid transparent',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => { 
          if (!isExpanded && !hasActiveChild) {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)';
            e.currentTarget.style.color = '#ffffff';
          }
        }}
        onMouseLeave={(e) => { 
          if (!isExpanded && !hasActiveChild) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'rgba(255,255,255,0.85)';
          }
        }}
      >
        <CategoryIcon size={18} style={{ opacity: isExpanded || hasActiveChild ? 1 : 0.7 }} />
        <span className="flex-1 text-left">{category.label}</span>
        <ChevronRight 
          size={14} 
          style={{ 
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            opacity: 0.7
          }}
        />
      </button>

      {/* Sous-items de la catégorie */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="ml-4 pl-3 space-y-0.5" style={{ borderLeft: '2px solid rgba(255,255,255,0.15)' }}>
              {category.subItems.map((item) => {
                const ItemIcon = item.icon;
                const isActive = location.pathname === item.path;
                const itemBadgeCount = badgeCounts[item.id] || 0;
                
                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    onClick={onClose}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm"
                    style={{ 
                      color: isActive ? '#ffffff' : 'rgba(255,255,255,0.7)',
                      backgroundColor: isActive ? 'rgba(255,255,255,0.12)' : 'transparent',
                      borderLeft: isActive ? '2px solid rgba(255,255,255,0.4)' : '2px solid transparent',
                      marginLeft: '-2px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => { 
                      if (!isActive) {
                        e.currentTarget.style.color = '#ffffff';
                        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)';
                      }
                    }}
                    onMouseLeave={(e) => { 
                      if (!isActive) {
                        e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <ItemIcon size={16} />
                    <span className="flex-1">{item.label}</span>
                    {itemBadgeCount > 0 && (
                      <span className="min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {itemBadgeCount > 99 ? '99+' : itemBadgeCount}
                      </span>
                    )}
                    {item.isNew && !itemBadgeCount && (
                      <span className="px-1.5 py-0.5 bg-[#E8650A] text-white text-[10px] font-bold rounded">
                        NEW
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Composant Sidebar - Design Premium Bleu Marine
function CockpitSidebar({ isOpen, onClose }) {
  const location = useLocation();
  const { user, initials, isSuperConfiance } = useCurrentUser();
  
  // État du sous-menu AbracadaBati
  const [isBatiExpanded, setIsBatiExpanded] = useState(true);
  
  // État accordéon pour les sous-sections (un seul ouvert à la fois)
  const [expandedCategory, setExpandedCategory] = useState(null); // TOUT FERMÉ par défaut
  
  // Compteur messages non lus dans les cercles
  const [cerclesUnreadCount, setCerclesUnreadCount] = useState(0);
  
  // Fetch cercles unread count
  useEffect(() => {
    const fetchCerclesCount = async () => {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token');
      if (!token) return;
      
      try {
        const res = await fetch(`${API_URL}/api/ecosystem/circles`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const circles = await res.json();
          const total = circles.reduce((sum, c) => sum + (c.unread_count || 0), 0);
          setCerclesUnreadCount(total);
        }
      } catch (e) {
        console.error('Error fetching circles count:', e);
      }
    };
    
    fetchCerclesCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchCerclesCount, 30000);
    return () => clearInterval(interval);
  }, []);
  
  // Fonction pour gérer l'accordéon
  const handleCategoryToggle = (categoryId) => {
    setExpandedCategory(prev => prev === categoryId ? null : categoryId);
  };

  return (
    <>
      {/* Overlay mobile - couvre tout l'écran y compris la bottom nav */}
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[10200] lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar - PLEIN ÉCRAN sur mobile (couvre la bottom nav) */}
      <aside className={`
        fixed lg:sticky top-0 left-0 z-[10201] lg:z-auto
        w-full lg:w-72 h-full lg:h-screen
        transform transition-transform duration-300 ease-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col shadow-2xl lg:shadow-none
      `}
      style={{ 
        backgroundColor: '#1a2a4a',
        paddingTop: 'max(env(safe-area-inset-top, 0px), 8px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}
      >
        {/* Zone Header - Fond Blanc */}
        <div className="bg-white" style={{ borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
          {/* Header avec logo et Bonjour */}
          <div className="p-4 pt-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3" onClick={onClose}>
                <div className="w-12 h-12 bg-gradient-to-br from-[#E8650A] to-[#d45a09] rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/20">
                  <Building2 size={24} className="text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold" style={{ color: '#1a2a4a' }}>Mon Cockpit</h1>
                  <p className="text-sm" style={{ color: '#888888' }}>
                    {user?.name ? `Bonjour ${user.name.split(' ')[0]}` : 'Bonjour les magiciens'}
                  </p>
                </div>
              </Link>
              <button 
                onClick={onClose}
                className="lg:hidden p-3 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors touch-manipulation"
                style={{ minWidth: '48px', minHeight: '48px' }}
              >
                <X size={24} className="text-gray-500" />
              </button>
            </div>
          </div>
        </div>

        {/* Contenu du menu - Fond bleu marine */}
        <div className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
          
          {/* ═══════════════════════════════════════════════════════════════════
              SECTION 1 — PROFIL
          ═══════════════════════════════════════════════════════════════════ */}
          <div className="px-3 pt-4">
            <p style={{ 
              color: 'rgba(255,255,255,0.35)',
              fontSize: '10px',
              fontWeight: '700',
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              padding: '10px 8px 4px'
            }}>
              Profil
            </p>
            <div className="space-y-1">
              {/* Actu */}
              <Link
                to="/cockpit-particulier/reseau"
                onClick={onClose}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all active:scale-[0.98] touch-manipulation"
                style={{ 
                  color: location.pathname === '/cockpit-particulier/reseau' ? '#E8650A' : '#ffffff',
                  backgroundColor: location.pathname === '/cockpit-particulier/reseau' ? 'rgba(232,101,10,0.15)' : 'transparent',
                  minHeight: '44px'
                }}
              >
                <Newspaper size={20} style={{ opacity: location.pathname === '/cockpit-particulier/reseau' ? 1 : 0.7 }} />
                <span className="font-medium">Actu</span>
              </Link>
              
              {/* Mes équipes */}
              <Link
                to="/cockpit-particulier/cercles"
                onClick={onClose}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all active:scale-[0.98] touch-manipulation"
                style={{ 
                  color: location.pathname === '/cockpit-particulier/cercles' ? '#E8650A' : '#ffffff',
                  backgroundColor: location.pathname === '/cockpit-particulier/cercles' ? 'rgba(232,101,10,0.15)' : 'transparent',
                  minHeight: '44px'
                }}
              >
                <Users size={20} style={{ opacity: location.pathname === '/cockpit-particulier/cercles' ? 1 : 0.7 }} />
                <span className="font-medium flex-1">Mes équipes</span>
                {cerclesUnreadCount > 0 && (
                  <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                    {cerclesUnreadCount}
                  </span>
                )}
              </Link>
            </div>
          </div>
          
          {/* Séparateur */}
          <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)', margin: '6px 16px' }} />
          
          {/* ═══════════════════════════════════════════════════════════════════
              SECTION 2 — COMMUNICATION
          ═══════════════════════════════════════════════════════════════════ */}
          <div className="px-3">
            <p style={{ 
              color: 'rgba(255,255,255,0.35)',
              fontSize: '10px',
              fontWeight: '700',
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              padding: '10px 8px 4px'
            }}>
              Communication
            </p>
            <div className="space-y-1">
              {/* Messages */}
              <Link
                to="/cockpit-particulier/messagerie"
                onClick={onClose}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all active:scale-[0.98] touch-manipulation"
                style={{ 
                  color: location.pathname === '/cockpit-particulier/messagerie' ? '#E8650A' : '#ffffff',
                  backgroundColor: location.pathname === '/cockpit-particulier/messagerie' ? 'rgba(232,101,10,0.15)' : 'transparent',
                  minHeight: '44px'
                }}
              >
                <MessageCircle size={20} style={{ opacity: location.pathname === '/cockpit-particulier/messagerie' ? 1 : 0.7 }} />
                <span className="font-medium">Messages</span>
              </Link>
              
              {/* Mails */}
              <Link
                to="/cockpit-particulier/mails"
                onClick={onClose}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all active:scale-[0.98] touch-manipulation"
                style={{ 
                  color: location.pathname === '/cockpit-particulier/mails' ? '#E8650A' : '#ffffff',
                  backgroundColor: location.pathname === '/cockpit-particulier/mails' ? 'rgba(232,101,10,0.15)' : 'transparent',
                  minHeight: '44px'
                }}
              >
                <Mail size={20} style={{ opacity: location.pathname === '/cockpit-particulier/mails' ? 1 : 0.7 }} />
                <span className="font-medium">Mails</span>
              </Link>
              
              {/* Visioconférence */}
              <Link
                to="/cockpit-particulier/visio"
                onClick={onClose}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all active:scale-[0.98] touch-manipulation"
                style={{ 
                  color: location.pathname === '/cockpit-particulier/visio' ? '#E8650A' : '#ffffff',
                  backgroundColor: location.pathname === '/cockpit-particulier/visio' ? 'rgba(232,101,10,0.15)' : 'transparent',
                  minHeight: '44px'
                }}
              >
                <Video size={20} style={{ opacity: location.pathname === '/cockpit-particulier/visio' ? 1 : 0.7 }} />
                <span className="font-medium">Visioconférence</span>
              </Link>
              
              {/* Abra IA - Super Assistant avec Artifacts */}
              <Link
                to="/cockpit-particulier/abra-ia"
                onClick={onClose}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all active:scale-[0.98] touch-manipulation"
                style={{ 
                  color: location.pathname === '/cockpit-particulier/abra-ia' ? '#E8650A' : '#ffffff',
                  backgroundColor: location.pathname === '/cockpit-particulier/abra-ia' ? 'rgba(232,101,10,0.15)' : 'transparent',
                  minHeight: '44px'
                }}
                data-testid="sidebar-abra-ia-particulier"
              >
                <div className="relative">
                  <Sparkles size={20} style={{ opacity: location.pathname === '/cockpit-particulier/abra-ia' ? 1 : 0.7 }} />
                </div>
                <span className="font-medium">Abra IA</span>
                <span style={{
                  marginLeft: 'auto',
                  padding: '2px 8px',
                  background: 'linear-gradient(135deg, #E8650A 0%, #d45a09 100%)',
                  color: '#ffffff',
                  fontSize: '9px',
                  fontWeight: '700',
                  borderRadius: '4px',
                  textTransform: 'uppercase'
                }}>
                  New
                </span>
              </Link>
            </div>
          </div>
          
          {/* Séparateur */}
          <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)', margin: '6px 16px' }} />
          
          {/* ═══════════════════════════════════════════════════════════════════
              SECTION 3 — ORGANISATION
          ═══════════════════════════════════════════════════════════════════ */}
          <div className="px-3">
            <p style={{ 
              color: 'rgba(255,255,255,0.35)',
              fontSize: '10px',
              fontWeight: '700',
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              padding: '10px 8px 4px'
            }}>
              Organisation
            </p>
            <div className="space-y-1">
              {/* Agenda */}
              <Link
                to="/cockpit-particulier/agenda"
                onClick={onClose}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all active:scale-[0.98] touch-manipulation"
                style={{ 
                  color: location.pathname === '/cockpit-particulier/agenda' ? '#E8650A' : '#ffffff',
                  backgroundColor: location.pathname === '/cockpit-particulier/agenda' ? 'rgba(232,101,10,0.15)' : 'transparent',
                  minHeight: '44px'
                }}
              >
                <Calendar size={20} style={{ opacity: location.pathname === '/cockpit-particulier/agenda' ? 1 : 0.7 }} />
                <span className="font-medium">Agenda</span>
              </Link>
              
              {/* Liste des Tâches */}
              <Link
                to="/cockpit-particulier/taches"
                onClick={onClose}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all active:scale-[0.98] touch-manipulation"
                style={{ 
                  color: location.pathname === '/cockpit-particulier/taches' ? '#E8650A' : '#ffffff',
                  backgroundColor: location.pathname === '/cockpit-particulier/taches' ? 'rgba(232,101,10,0.15)' : 'transparent',
                  minHeight: '44px'
                }}
              >
                <CheckCircle size={20} style={{ opacity: location.pathname === '/cockpit-particulier/taches' ? 1 : 0.7 }} />
                <span className="font-medium">Liste des Tâches</span>
              </Link>
              
              {/* Notes */}
              <Link
                to="/cockpit-particulier/notes"
                onClick={onClose}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all active:scale-[0.98] touch-manipulation"
                style={{ 
                  color: location.pathname === '/cockpit-particulier/notes' ? '#E8650A' : '#ffffff',
                  backgroundColor: location.pathname === '/cockpit-particulier/notes' ? 'rgba(232,101,10,0.15)' : 'transparent',
                  minHeight: '44px'
                }}
              >
                <FileText size={20} style={{ opacity: location.pathname === '/cockpit-particulier/notes' ? 1 : 0.7 }} />
                <span className="font-medium">Notes</span>
              </Link>
              
              {/* Boîte à idées */}
              <Link
                to="/cockpit-particulier/boite-a-idees"
                onClick={onClose}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all active:scale-[0.98] touch-manipulation"
                style={{ 
                  color: location.pathname === '/cockpit-particulier/boite-a-idees' ? '#E8650A' : '#ffffff',
                  backgroundColor: location.pathname === '/cockpit-particulier/boite-a-idees' ? 'rgba(232,101,10,0.15)' : 'transparent',
                  minHeight: '44px'
                }}
              >
                <Lightbulb size={20} style={{ opacity: location.pathname === '/cockpit-particulier/boite-a-idees' ? 1 : 0.7 }} />
                <span className="font-medium">Boîte à idées</span>
              </Link>
            </div>
          </div>
          
          {/* Séparateur */}
          <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)', margin: '6px 16px' }} />
          
          {/* ═══════════════════════════════════════════════════════════════════
              SECTION 4 — STOCKAGE
          ═══════════════════════════════════════════════════════════════════ */}
          <div className="px-3">
            <p style={{ 
              color: 'rgba(255,255,255,0.35)',
              fontSize: '10px',
              fontWeight: '700',
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              padding: '10px 8px 4px'
            }}>
              Stockage
            </p>
            <div className="space-y-1">
              {/* Documents */}
              <Link
                to="/cockpit-particulier/documents"
                onClick={onClose}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all active:scale-[0.98] touch-manipulation"
                style={{ 
                  color: location.pathname === '/cockpit-particulier/documents' ? '#E8650A' : '#ffffff',
                  backgroundColor: location.pathname === '/cockpit-particulier/documents' ? 'rgba(232,101,10,0.15)' : 'transparent',
                  minHeight: '44px'
                }}
              >
                <FolderOpen size={20} style={{ opacity: location.pathname === '/cockpit-particulier/documents' ? 1 : 0.7 }} />
                <span className="font-medium">Documents</span>
              </Link>
              
              {/* Coffre-fort (Bientôt) */}
              <div
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm cursor-not-allowed"
                style={{ color: 'rgba(255,255,255,0.5)', minHeight: '44px' }}
              >
                <Lock size={20} style={{ opacity: 0.5 }} />
                <span className="font-medium flex-1">Coffre-fort</span>
                <span style={{ 
                  backgroundColor: 'rgba(232,101,10,0.15)',
                  color: '#E8650A',
                  border: '1px solid rgba(232,101,10,0.3)',
                  borderRadius: '20px',
                  padding: '2px 8px',
                  fontSize: '10px'
                }}>Bientôt</span>
              </div>
            </div>
          </div>
          
          {/* Séparateur */}
          <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)', margin: '6px 16px' }} />
          
          {/* ═══════════════════════════════════════════════════════════════════
              SECTION 5 — VIE PRATIQUE
          ═══════════════════════════════════════════════════════════════════ */}
          <div className="px-3">
            <p style={{ 
              color: 'rgba(255,255,255,0.35)',
              fontSize: '10px',
              fontWeight: '700',
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              padding: '10px 8px 4px'
            }}>
              Vie pratique
            </p>
            <div className="space-y-1">
              {/* Liste de courses (Bientôt) */}
              <div
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm cursor-not-allowed"
                style={{ color: 'rgba(255,255,255,0.5)', minHeight: '44px' }}
              >
                <ShoppingCart size={20} style={{ opacity: 0.5 }} />
                <span className="font-medium flex-1">Liste de courses</span>
                <span style={{ 
                  backgroundColor: 'rgba(232,101,10,0.15)',
                  color: '#E8650A',
                  border: '1px solid rgba(232,101,10,0.3)',
                  borderRadius: '20px',
                  padding: '2px 8px',
                  fontSize: '10px'
                }}>Bientôt</span>
              </div>
              
              {/* Partage de dépenses (Bientôt) */}
              <div
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm cursor-not-allowed"
                style={{ color: 'rgba(255,255,255,0.5)', minHeight: '44px' }}
              >
                <CreditCard size={20} style={{ opacity: 0.5 }} />
                <span className="font-medium flex-1">Partage de dépenses</span>
                <span style={{ 
                  backgroundColor: 'rgba(232,101,10,0.15)',
                  color: '#E8650A',
                  border: '1px solid rgba(232,101,10,0.3)',
                  borderRadius: '20px',
                  padding: '2px 8px',
                  fontSize: '10px'
                }}>Bientôt</span>
              </div>
            </div>
          </div>
          
          {/* Séparateur */}
          <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)', margin: '6px 16px' }} />
          
          {/* ═══════════════════════════════════════════════════════════════════
              SECTION 6 — MES UNIVERS
          ═══════════════════════════════════════════════════════════════════ */}
          <div className="px-3 pb-4">
            <p style={{ 
              color: 'rgba(255,255,255,0.35)',
              fontSize: '10px',
              fontWeight: '700',
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              padding: '10px 8px 4px'
            }}>
              Mes Univers
            </p>
            
            {/* AbracadaBati - Univers Actif */}
            <div className="mb-2">
              <button
                onClick={() => setIsBatiExpanded(!isBatiExpanded)}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-bold transition-all"
                style={{ 
                  color: '#E8650A',
                  backgroundColor: 'rgba(232,101,10,0.1)',
                  borderLeft: '3px solid #E8650A'
                }}
              >
                <Building2 size={20} style={{ color: '#E8650A' }} />
                <span className="flex-1 text-left">AbracadaBati</span>
                <ChevronDown 
                  size={18} 
                  className={`transition-transform duration-200 ${isBatiExpanded ? '' : '-rotate-90'}`}
                  style={{ color: '#E8650A' }}
                />
              </button>

              {/* Sous-catégories AbracadaBati */}
              <AnimatePresence>
                {isBatiExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    className="overflow-hidden"
                  >
                    <div className="ml-2 mt-1 space-y-1">
                      {Object.values(ABRACADABATI_CATEGORIES).map((category) => (
                        <BatiCategoryItem
                          key={category.id}
                          category={category}
                          location={location}
                          onClose={onClose}
                          isExpanded={expandedCategory === category.id}
                          onToggle={() => handleCategoryToggle(category.id)}
                          badgeCounts={{ 'r-cercles': cerclesUnreadCount }}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mon Cercle de Confiance - Visible uniquement pour Super Confiance */}
            {isSuperConfiance && (
              <div className="px-3 py-2">
                <Link
                  to="/cockpit-particulier/mon-cercle-confiance"
                  onClick={onClose}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-all"
                  style={{ 
                    color: location.pathname === '/cockpit-particulier/mon-cercle-confiance' ? '#ffffff' : '#ffffff',
                    background: location.pathname === '/cockpit-particulier/mon-cercle-confiance' 
                      ? 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)' 
                      : 'linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(168,85,247,0.1) 100%)',
                    border: '1px solid rgba(139,92,246,0.3)',
                    fontWeight: 600
                  }}
                  onMouseEnter={(e) => { 
                    if (location.pathname !== '/cockpit-particulier/mon-cercle-confiance') {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139,92,246,0.25) 0%, rgba(168,85,247,0.2) 100%)';
                    }
                  }}
                  onMouseLeave={(e) => { 
                    if (location.pathname !== '/cockpit-particulier/mon-cercle-confiance') {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(168,85,247,0.1) 100%)';
                    }
                  }}
                >
                  <Crown size={18} style={{ color: '#a855f7' }} />
                  <span className="flex-1">Mon Cercle de Confiance</span>
                  <span 
                    style={{ 
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                      color: 'white',
                      borderRadius: '20px',
                      padding: '2px 8px',
                      fontSize: '10px',
                      fontWeight: 700
                    }}
                  >
                    VIP
                  </span>
                </Link>
              </div>
            )}

            {/* Univers "Bientôt" */}
            <div className="space-y-0.5 mt-2">
              {COMING_SOON_UNIVERS.map((univers) => (
                <div
                  key={univers.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm"
                  style={{ 
                    color: 'rgba(255,255,255,0.35)',
                    cursor: 'not-allowed',
                    opacity: 0.5
                  }}
                >
                  <span style={{ fontSize: '16px', opacity: 0.5 }}>{univers.emoji}</span>
                  <span className="flex-1">{univers.officialName}</span>
                  <span 
                    style={{ 
                      backgroundColor: 'rgba(232,101,10,0.15)',
                      color: '#E8650A',
                      border: '1px solid rgba(232,101,10,0.3)',
                      borderRadius: '20px',
                      padding: '2px 8px',
                      fontSize: '10px'
                    }}
                  >
                    Bientôt
                  </span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Séparateur */}
          <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)', margin: '6px 16px' }} />

          {/* ═══════════════════════════════════════════════════════════════════
              ESPACE PRO - Bouton pour découvrir le Cockpit Pro
          ═══════════════════════════════════════════════════════════════════ */}
          <div className="px-3 py-2">
            <Link
              to="/cockpit-pro"
              onClick={onClose}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm transition-all"
              style={{ 
                background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                color: '#ffffff',
                boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
              }}
              onMouseEnter={(e) => { 
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(34, 197, 94, 0.4)';
              }}
              onMouseLeave={(e) => { 
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(34, 197, 94, 0.3)';
              }}
              data-testid="sidebar-espace-pro-btn"
            >
              <Briefcase size={20} />
              <span className="flex-1 font-semibold">Espace Pro</span>
              <ChevronRight size={18} style={{ opacity: 0.8 }} />
            </Link>
          </div>

          {/* Paramètres */}
          <div className="px-3 py-2">
            <Link
              to="/cockpit-particulier/parametres"
              onClick={onClose}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-all"
              style={{ 
                color: location.pathname === '/cockpit-particulier/parametres' ? '#E8650A' : '#ffffff',
                backgroundColor: location.pathname === '/cockpit-particulier/parametres' ? 'rgba(232,101,10,0.1)' : 'transparent'
              }}
              onMouseEnter={(e) => { if (location.pathname !== '/cockpit-particulier/parametres') e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)' }}
              onMouseLeave={(e) => { if (location.pathname !== '/cockpit-particulier/parametres') e.currentTarget.style.backgroundColor = 'transparent' }}
            >
              <Settings size={20} style={{ opacity: location.pathname === '/cockpit-particulier/parametres' ? 1 : 0.7 }} />
              <span>Paramètres</span>
            </Link>
          </div>
        </div>

        {/* Footer - Déconnexion */}
        <div className="p-4 safe-area-bottom" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <button 
            onClick={() => {
              localStorage.removeItem('buyer_token');
              localStorage.removeItem('ecosystem_token');
              localStorage.removeItem('client_token');
              localStorage.removeItem('client_user');
              window.location.href = '/';
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-colors text-sm font-medium"
            style={{ 
              color: '#ef4444',
              backgroundColor: 'rgba(239,68,68,0.1)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.1)'}
            data-testid="cockpit-particulier-logout"
          >
            <LogOut size={18} />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>
    </>
  );
}

// Invitation pour les visiteurs non connectés — conservée pour réutilisation future
// eslint-disable-next-line no-unused-vars
function CockpitInvitation() {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 text-center"
      style={{ background: 'linear-gradient(180deg, #1E3A5F 0%, #152a45 100%)' }}
    >
      <img
        src="/icons/abracada-logo.svg"
        alt="Abracada"
        className="w-20 h-20 rounded-3xl mb-6 shadow-lg"
      />
      <h1
        className="text-2xl font-bold text-white mb-2"
        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      >
        Bienvenue dans Abracada
      </h1>
      <p className="text-white/70 text-sm max-w-sm mb-8">
        Ton espace personnel piloté par Abra : tes devis, tes artisans, tes
        annonces, tes messages et ton réseau — tout au même endroit.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs sm:max-w-none sm:w-auto">
        <Link
          to="/connexion"
          className="px-6 py-3 rounded-xl text-white font-semibold shadow-md text-center"
          style={{ background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)' }}
        >
          Se connecter
        </Link>
        <Link
          to="/reseau/inscription"
          className="px-6 py-3 rounded-xl font-semibold text-white text-center border border-white/30 hover:bg-white/10 transition-colors"
        >
          Créer mon compte gratuit
        </Link>
      </div>
      <Link to="/" className="mt-6 text-white/50 text-sm hover:text-white/80">
        ← Retour à l'accueil
      </Link>
    </div>
  );
}

// Page principale du Cockpit
export default function CockpitParticulierLayout({ contexteInitial, sidebarReplieeInitiale }) {
  // Design unifié ChatGPT/Claude pour Desktop ET Mobile
  // Le composant CockpitAssistantUnified gère lui-même le responsive
  // IMPORTANT: Les sous-routes passent par <Outlet /> avec la navbar incluse

  // Cockpit ouvert à tous (décision Romain 11/06) : les visiteurs non connectés
  // voient tout ; la barre du haut leur propose « Se connecter / S'inscrire »
  // et le contenu personnel déclenche le pop-up d'inscription.
  return (
    <>
      <CockpitAssistantUnified
        userType="particulier"
        contexteInitial={contexteInitial}
        sidebarReplieeInitiale={sidebarReplieeInitiale}
      />
    </>
  );
}

// Layout mobile classique (ancien CockpitParticulierLayout)
function CockpitParticulierMobileLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
  const [registerActionLabel, setRegisterActionLabel] = useState('cette fonctionnalité');
  const [showUniversDrawer, setShowUniversDrawer] = useState(false);
  const [selectedUnivers, setSelectedUnivers] = useState(null); // Pour le popup Bientôt
  const [showPlusDrawer, setShowPlusDrawer] = useState(false); // Drawer "Plus" de la bottom nav
  const [assistantMessage, setAssistantMessage] = useState('');
  const [showVoiceNotes, setShowVoiceNotes] = useState(false);
  const [showAssistantChat, setShowAssistantChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [isAssistantTyping, setIsAssistantTyping] = useState(false);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [selectedMessageContent, setSelectedMessageContent] = useState('');
  const [sendMode, setSendMode] = useState(null); // 'message' | 'email'
  const [crmContacts, setCrmContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [showArtifactsPanel, setShowArtifactsPanel] = useState(false);
  const [activeArtifact, setActiveArtifact] = useState(null);
  const [showArtifactViewer, setShowArtifactViewer] = useState(false);
  // États pour l'upload de fichiers (mobile)
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const location = useLocation();
  const { user, isLoggedIn } = useCurrentUser();
  const { isHidden } = useImmersiveScrollContext(); // Pour masquer la nav au scroll
  
  // Compteurs pour les badges - initialisés à 0 pour production
  const [cerclesUnreadCount, setCerclesUnreadCount] = useState(0);
  const [messagesUnreadCount, setMessagesUnreadCount] = useState(0);
  const [mailsUnreadCount, setMailsUnreadCount] = useState(0);
  const [tasksOverdueCount, setTasksOverdueCount] = useState(0);
  
  // Fetch cercles unread count pour la bottom nav
  useEffect(() => {
    const fetchCerclesCount = async () => {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token');
      if (!token) return;
      
      try {
        const res = await fetch(`${API_URL}/api/ecosystem/circles`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const circles = await res.json();
          const total = circles.reduce((sum, c) => sum + (c.unread_count || 0), 0);
          setCerclesUnreadCount(total);
        }
      } catch (e) {
        console.error('Error fetching circles count:', e);
      }
    };
    
    fetchCerclesCount();
    const interval = setInterval(fetchCerclesCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fonction pour gérer les actions protégées
  const handleProtectedAction = (actionLabel, callback) => {
    if (isLoggedIn) {
      callback();
    } else {
      setRegisterActionLabel(actionLabel);
      setShowRegisterPrompt(true);
    }
  };

  // Fermer la sidebar sur changement de route (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // ═══════════════════════════════════════════════════════════════════════════
  // FONCTIONS ASSISTANT CHAT
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Charger les contacts CRM
  const fetchCrmContacts = async () => {
    setContactsLoading(true);
    try {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token') || localStorage.getItem('client_token');
      const res = await fetch(`${API_URL}/api/crm/contacts/all`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setCrmContacts(data.contacts || []);
      } else {
        // Fallback: données de démo si pas de contacts
        setCrmContacts([
          { id: 'demo-1', name: 'Jean Dupont', email: 'jean.dupont@email.fr', type: 'client' },
          { id: 'demo-2', name: 'Marie Martin', email: 'marie.martin@email.fr', type: 'prospect' },
          { id: 'demo-3', name: 'Pierre Durand', email: 'pierre.durand@email.fr', phone: '06 12 34 56 78', type: 'client' },
        ]);
      }
    } catch (e) {
      console.error('Erreur chargement contacts:', e);
      // Fallback démo
      setCrmContacts([
        { id: 'demo-1', name: 'Jean Dupont', email: 'jean.dupont@email.fr', type: 'client' },
        { id: 'demo-2', name: 'Marie Martin', email: 'marie.martin@email.fr', type: 'prospect' },
      ]);
    } finally {
      setContactsLoading(false);
    }
  };

  // Envoyer un message à l'assistant - CONNEXION API RÉELLE
  const handleSendAssistantMessage = async () => {
    if (!assistantMessage.trim()) return;
    
    const userMessage = assistantMessage.trim();
    
    // Ajouter le message utilisateur
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setShowAssistantChat(true);
    setIsAssistantTyping(true);
    setAssistantMessage('');
    
    try {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token');
      const userId = (() => {
        const ecosystemUser = localStorage.getItem('ecosystem_user');
        const clientUser = localStorage.getItem('client_user');
        if (ecosystemUser) {
          try { return JSON.parse(ecosystemUser).id; } catch(e) {}
        }
        if (clientUser) {
          try { return JSON.parse(clientUser).id; } catch(e) {}
        }
        return localStorage.getItem('userId') || 'demo_user';
      })();
      
      // Appel API réel à l'assistant IA
      const res = await fetch(`${API_URL}/api/assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          message: userMessage,
          user_id: userId,
          context: {
            user_type: 'particulier',
            platform: 'mobile'
          }
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        
        // Construire l'artifact si présent dans la réponse
        let artifact = null;
        if (data.has_artifact && data.artifact) {
          artifact = {
            type: data.artifact.type,
            title: data.artifact.title || 'Résultat',
            subtitle: data.artifact.subtitle || '',
            preview: data.artifact.preview || '',
            data: data.artifact.data || data.artifact
          };
        }
        
        setChatMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.response || data.message,
          artifact: artifact
        }]);
      } else {
        throw new Error('Erreur API');
      }
    } catch (e) {
      console.error('Erreur assistant:', e);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Désolé, je rencontre un problème technique. Réessaie dans quelques instants.'
      }]);
    } finally {
      setIsAssistantTyping(false);
    }
  };

  // Ouvrir un artifact dans le viewer
  const handleOpenArtifact = (artifact) => {
    setActiveArtifact(artifact);
    setShowArtifactViewer(true);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // HANDLERS UPLOAD DE FICHIERS (Mobile Particulier)
  // ═══════════════════════════════════════════════════════════════════════════
  
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setIsUploading(true);
    setShowAttachMenu(false);
    
    const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token');
    
    for (const file of files) {
      try {
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');
        const isPDF = file.type === 'application/pdf';
        
        // Upload le fichier
        const formData = new FormData();
        formData.append('file', file);
        
        const uploadRes = await fetch(`${API_URL}/api/upload/image`, {
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: formData
        });
        
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          
          const newFile = {
            id: uploadData.id || `file-${Date.now()}`,
            name: file.name,
            url: uploadData.url || uploadData.image_url,
            type: isImage ? 'image' : isVideo ? 'video' : isPDF ? 'pdf' : 'document',
          };
          
          setUploadedFiles(prev => [...prev, newFile]);
          
          // Créer le message avec analyse contextuelle
          let analysisPrompt = `J'ai uploadé un fichier : "${file.name}"`;
          const fileName = file.name.toLowerCase();
          
          if (isImage) {
            if (fileName.includes('facture')) {
              analysisPrompt = `J'ai uploadé une image d'une facture : "${file.name}". Peux-tu l'analyser ?`;
            } else {
              analysisPrompt = `J'ai uploadé cette image : "${file.name}". Peux-tu me dire ce que tu vois ?`;
            }
          } else if (isPDF) {
            analysisPrompt = `J'ai uploadé ce document PDF : "${file.name}". Peux-tu l'analyser ?`;
          }
          
          // Ajouter le message utilisateur avec l'attachment
          setChatMessages(prev => [...prev, { 
            role: 'user', 
            content: analysisPrompt,
            attachment: newFile
          }]);
          setShowAssistantChat(true);
          setIsAssistantTyping(true);
          
          // Envoyer à l'IA pour analyse
          const userId = user?.id || 'demo_user';
          
          const analysisRes = await fetch(`${API_URL}/api/assistant/chat`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
              message: analysisPrompt,
              user_id: userId,
              context: {
                user_type: 'particulier',
                platform: 'mobile',
                attachment: {
                  type: newFile.type,
                  url: newFile.url,
                  name: newFile.name
                }
              }
            })
          });
          
          if (analysisRes.ok) {
            const data = await analysisRes.json();
            setChatMessages(prev => [...prev, { 
              role: 'assistant', 
              content: data.response || data.message
            }]);
          }
        }
      } catch (error) {
        console.error('Erreur upload:', error);
      }
    }
    
    setIsUploading(false);
    setUploadedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Actions sur un artifact
  const handleArtifactAction = (action, artifact) => {
    switch (action) {
      case 'download':
        console.log('Télécharger:', artifact);
        break;
      case 'share':
        console.log('Partager:', artifact);
        break;
      case 'edit':
        console.log('Modifier:', artifact);
        break;
      case 'open':
        console.log('Ouvrir:', artifact);
        break;
      default:
        break;
    }
  };

  // Envoyer le message à un contact
  const handleSendToContact = async (contact) => {
    if (sendMode === 'email') {
      // Ouvrir mailto avec le contenu
      const subject = encodeURIComponent('Message de votre assistant');
      const body = encodeURIComponent(selectedMessageContent);
      window.open(`mailto:${contact.email}?subject=${subject}&body=${body}`);
      setShowContactPicker(false);
    } else {
      // Envoyer via API AbracadaChat
      try {
        const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token') || localStorage.getItem('client_token');
        const res = await fetch(`${API_URL}/api/crm/contacts/${contact.id}/send-message?message=${encodeURIComponent(selectedMessageContent)}`, {
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        if (res.ok) {
          const data = await res.json();
          alert(`✅ Message envoyé à ${contact.name} !`);
        } else {
          alert(`Message envoyé à ${contact.name} (mode démo)`);
        }
      } catch (e) {
        console.error('Erreur envoi message:', e);
        alert(`Message envoyé à ${contact.name} (mode démo)`);
      }
      setShowContactPicker(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex w-full max-w-full overflow-x-hidden">
      {/* Sidebar */}
      <CockpitSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Popup d'inscription */}
      <RegisterPromptModal 
        isOpen={showRegisterPrompt} 
        onClose={() => setShowRegisterPrompt(false)}
        actionLabel={registerActionLabel}
      />

      {/* Publish Modal - Skip profile choice since we're in Particulier cockpit */}
      <PublishModal 
        isOpen={showPublishModal} 
        onClose={() => setShowPublishModal(false)} 
        isLoggedIn={isLoggedIn}
        skipProfileChoice={true}
        defaultProfile="particulier"
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen w-full max-w-full overflow-x-hidden">
        {/* Top Bar - Desktop & Tablet */}
        <header className="hidden lg:flex bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 px-6 py-3 items-center justify-between sticky top-0 z-30">
          {/* Left - Navigation vers Shop et Réseau */}
          <div className="flex items-center gap-3">
            <Link to="/">
              <button className="flex items-center gap-2 px-4 py-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 font-medium rounded-xl transition-colors">
                <ShoppingBag size={18} />
                <span>Shop</span>
              </button>
            </Link>
            <Link to="/reseau">
              <button className="flex items-center gap-2 px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 font-medium rounded-xl transition-colors">
                <Users size={18} />
                <span>Réseau</span>
              </button>
            </Link>
          </div>
          
          {/* Right - Actions - Ordre: Mode sombre, Messages, Notifications, Particulier */}
          <div className="flex items-center gap-2">
            {/* Dark Mode Toggle */}
            <ThemeToggle />
            
            {/* Messages avec badge */}
            <button 
              onClick={() => window.location.href = '/cockpit-particulier/messagerie'}
              className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="header-messages-btn"
            >
              <MessageCircle size={18} className="text-gray-500" strokeWidth={1.6} />
              <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 bg-[#F97316] rounded-full flex items-center justify-center">
                <span className="text-[9px] font-bold text-white">3</span>
              </span>
            </button>
            
            {/* Notification Bell */}
            <NotificationsBell token={localStorage.getItem('buyer_token') || localStorage.getItem('client_token')} accentColor="#3B82F6" />
            <NotificationBell />
            
            {/* Credit Badge - Particulier avec coins */}
            <AbracaCoinsBadge />
          </div>
        </header>
        
        {/* Menu de navigation principal - Desktop uniquement */}
        <CockpitMainNav />

        {/* Top Bar Mobile - FIXÉ SOUS le bandeau Bêta (36px sur mobile/tablette, 40px desktop) */}
        <header 
          className="lg:hidden bg-white/95 backdrop-blur-lg border-b border-gray-100 px-4 py-3 fixed left-0 right-0 z-30" 
          style={{ 
            top: '36px'
          }}
        >
          <div className="flex items-center justify-between">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2.5 -ml-1 hover:bg-gray-100 active:bg-gray-200 active:scale-95 rounded-xl transition-all touch-manipulation"
              aria-label="Menu"
              style={{ minWidth: '44px', minHeight: '44px' }}
            >
              <Menu size={24} className="text-gray-700" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-sm">
                <Building2 size={18} className="text-white" />
              </div>
              <span className="text-lg font-bold text-gray-900">Cockpit</span>
            </div>
            <div className="flex items-center gap-1">
              {/* Dark Mode Toggle */}
              <ThemeToggle />
              
              {/* Messages avec badge */}
              <button
                onClick={() => window.location.href = '/cockpit-particulier/messagerie'}
                className="relative p-2 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-all"
                aria-label="Messages"
                data-testid="header-messages-btn-mobile"
              >
                <MessageCircle size={20} className="text-[#1E3A5F]" strokeWidth={1.6} />
                <span className="absolute top-1 right-1 min-w-[14px] h-[14px] px-0.5 bg-[#F97316] rounded-full flex items-center justify-center">
                  <span className="text-[8px] font-bold text-white">3</span>
                </span>
              </button>
              
              {/* Notifications */}
              <NotificationsBell token={localStorage.getItem('buyer_token') || localStorage.getItem('client_token')} accentColor="#3B82F6" />
              <NotificationBell />
              
              {/* Credit Badge - Particulier */}
              <AbracaCoinsBadge />
            </div>
          </div>
        </header>

        {/* ═══ BARRE DE CHAT SUPER ASSISTANT (Mobile uniquement) ═══ */}
        <div 
          className="lg:hidden fixed left-0 right-0"
          style={{ 
            bottom: 'calc(60px + env(safe-area-inset-bottom))',
            zIndex: 10099,
          }}
        >
          {/* Zone de chat expandable */}
          <AnimatePresence>
            {showAssistantChat && chatMessages.length > 0 && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mx-3 mb-2 bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
                style={{ maxHeight: '300px' }}
              >
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                  <span className="text-xs font-semibold text-[#1E3A5F]">Assistant</span>
                  <button 
                    onClick={() => setShowAssistantChat(false)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <X size={14} className="text-gray-400" />
                  </button>
                </div>
                <div className="p-3 overflow-y-auto" style={{ maxHeight: '240px' }}>
                  {chatMessages.map((msg, idx) => (
                    <div key={idx} className={`mb-3 ${msg.role === 'user' ? 'text-right' : ''}`}>
                      {msg.role === 'assistant' ? (
                        <div>
                          <div className="inline-block bg-gray-100 rounded-xl rounded-tl-sm px-3 py-2 text-sm text-gray-800 max-w-[90%] text-left">
                            {msg.content}
                          </div>
                          
                          {/* Carte Artifact cliquable (style Claude) */}
                          {msg.artifact && (
                            <ArtifactCard 
                              artifact={msg.artifact}
                              onClick={handleOpenArtifact}
                            />
                          )}
                          
                          {/* Boutons d'action : Copier / Message / Mail */}
                          <div className="flex items-center gap-1 mt-1">
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(msg.content);
                              }}
                              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors group"
                              title="Copier"
                              data-testid="chat-action-copy"
                            >
                              <Copy size={14} className="text-gray-400 group-hover:text-[#1E3A5F]" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedMessageContent(msg.content);
                                setSendMode('message');
                                setShowContactPicker(true);
                                // Charger les contacts CRM
                                fetchCrmContacts();
                              }}
                              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors group"
                              title="Envoyer par message"
                              data-testid="chat-action-message"
                            >
                              <MessageCircle size={14} className="text-gray-400 group-hover:text-[#1E3A5F]" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedMessageContent(msg.content);
                                setSendMode('email');
                                setShowContactPicker(true);
                                // Charger les contacts CRM
                                fetchCrmContacts();
                              }}
                              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors group"
                              title="Envoyer par email"
                              data-testid="chat-action-email"
                            >
                              <Mail size={14} className="text-gray-400 group-hover:text-[#1E3A5F]" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="inline-block bg-[#1E3A5F] text-white rounded-xl rounded-tr-sm px-3 py-2 text-sm max-w-[85%]">
                          {msg.content}
                        </div>
                      )}
                    </div>
                  ))}
                  {isAssistantTyping && (
                    <div className="flex items-center gap-1 text-gray-400 text-sm">
                      <div className="flex gap-1">
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Barre de saisie compacte */}
          <div 
            className="mx-3 mb-2 rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
            style={{ background: 'white' }}
          >
            {/* Fichiers uploadés en attente */}
            {uploadedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 px-3 pt-2">
                {uploadedFiles.map(file => (
                  <div key={file.id} className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-lg text-xs">
                    {file.type === 'image' && <ImageIcon size={12} className="text-blue-500" />}
                    {file.type === 'video' && <Film size={12} className="text-purple-500" />}
                    {file.type === 'pdf' && <FileText size={12} className="text-red-500" />}
                    {file.type === 'document' && <File size={12} className="text-green-500" />}
                    <span className="truncate max-w-[100px]">{file.name}</span>
                    <button onClick={() => setUploadedFiles(prev => prev.filter(f => f.id !== file.id))}>
                      <X size={12} className="text-gray-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex items-center gap-2 p-2">
              {/* Bouton + pour ajouter des fichiers */}
              <div className="relative">
                <button
                  onClick={() => setShowAttachMenu(!showAttachMenu)}
                  disabled={isUploading}
                  className={`p-2 rounded-xl transition-colors ${
                    showAttachMenu 
                      ? 'bg-[#E8650A] text-white' 
                      : 'hover:bg-gray-100 text-[#1E3A5F]'
                  }`}
                >
                  {isUploading ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Plus size={18} />
                  )}
                </button>
                
                {/* Menu dropdown pour les attachments */}
                <AnimatePresence>
                  {showAttachMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50"
                    >
                      <div className="p-2">
                        <p className="px-2 py-1 text-xs font-medium text-gray-400 uppercase">
                          Importer
                        </p>
                        <button
                          onClick={() => {
                            fileInputRef.current?.setAttribute('accept', 'image/*');
                            fileInputRef.current?.click();
                          }}
                          className="w-full flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded-lg"
                        >
                          <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
                            <ImageIcon size={14} className="text-blue-600" />
                          </div>
                          <span className="text-sm text-gray-800">Photos</span>
                        </button>
                        <button
                          onClick={() => {
                            fileInputRef.current?.setAttribute('accept', '.pdf,.doc,.docx');
                            fileInputRef.current?.click();
                          }}
                          className="w-full flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded-lg"
                        >
                          <div className="w-7 h-7 rounded-lg bg-red-100 flex items-center justify-center">
                            <FileText size={14} className="text-red-600" />
                          </div>
                          <span className="text-sm text-gray-800">Documents</span>
                        </button>
                        <button
                          onClick={() => {
                            fileInputRef.current?.setAttribute('accept', 'video/*');
                            fileInputRef.current?.click();
                          }}
                          className="w-full flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded-lg"
                        >
                          <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center">
                            <Film size={14} className="text-purple-600" />
                          </div>
                          <span className="text-sm text-gray-800">Vidéos</span>
                        </button>
                        <button
                          onClick={() => {
                            fileInputRef.current?.setAttribute('accept', '*/*');
                            fileInputRef.current?.click();
                          }}
                          className="w-full flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded-lg"
                        >
                          <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Paperclip size={14} className="text-gray-600" />
                          </div>
                          <span className="text-sm text-gray-800">Autres</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {/* Input file caché */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </div>
              
              {/* Input */}
              <input
                type="text"
                value={assistantMessage}
                onChange={(e) => setAssistantMessage(e.target.value)}
                placeholder="Demande à ton assistant..."
                className="flex-1 bg-gray-100 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8650A] focus:bg-white transition-all"
                data-testid="assistant-input-particulier"
                disabled={isUploading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && assistantMessage.trim()) {
                    handleSendAssistantMessage();
                  }
                }}
              />
              
              {/* Bouton Micro */}
              <button 
                onClick={() => setShowVoiceNotes(true)}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <Mic size={18} className="text-[#1E3A5F]" />
              </button>
              
              {/* Bouton Envoyer */}
              <button 
                className={`p-2 rounded-xl transition-all ${
                  assistantMessage.trim() 
                    ? 'bg-[#E8650A] hover:bg-[#d45a09]' 
                    : 'bg-gray-200'
                }`}
                onClick={handleSendAssistantMessage}
                disabled={isUploading}
                data-testid="assistant-send-btn-particulier"
              >
                <Send size={16} className={assistantMessage.trim() ? 'text-white' : 'text-gray-400'} />
              </button>
            </div>
          </div>
        </div>

        {/* Modal de sélection de contact pour envoi Message/Email */}
        <AnimatePresence>
          {showContactPicker && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 md:left-[288px] bg-black/50 z-[10200] flex items-end justify-center"
              onClick={() => setShowContactPicker(false)}
            >
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="w-full max-w-lg bg-white rounded-t-2xl p-4"
                style={{ maxHeight: '60vh' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-[#1E3A5F]">
                    {sendMode === 'email' ? 'Envoyer par email à...' : 'Envoyer un message à...'}
                  </h3>
                  <button onClick={() => setShowContactPicker(false)} className="p-1">
                    <X size={20} className="text-gray-400" />
                  </button>
                </div>
                
                {/* Aperçu du message */}
                <div className="bg-gray-100 rounded-xl p-3 mb-4 text-sm text-gray-600 max-h-20 overflow-y-auto">
                  {selectedMessageContent.substring(0, 150)}{selectedMessageContent.length > 150 ? '...' : ''}
                </div>
                
                {/* Liste des contacts */}
                <div className="space-y-2 overflow-y-auto" style={{ maxHeight: '200px' }}>
                  <p className="text-xs text-gray-400 uppercase font-semibold mb-2">Contacts récents</p>
                  
                  {contactsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="w-6 h-6 border-2 border-[#1E3A5F] border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : crmContacts.length > 0 ? (
                    crmContacts.map(contact => (
                      <button
                        key={contact.id}
                        onClick={() => handleSendToContact(contact)}
                        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors"
                        data-testid={`contact-${contact.id}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white font-semibold">
                          {contact.name?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1 text-left">
                          <p className="font-medium text-gray-800">{contact.name}</p>
                          <p className="text-xs text-gray-500">{contact.email || contact.phone || 'Pas de contact'}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded ${contact.type === 'client' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                          {contact.type === 'client' ? 'Client' : 'Prospect'}
                        </span>
                      </button>
                    ))
                  ) : (
                    <p className="text-center text-gray-500 py-4">Aucun contact trouvé</p>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ═══════════════════════════════════════════════════════════════════
            BOUTON FLOTTANT ESPACE PRO (Mobile)
            Visible en permanence pour accéder au Cockpit Pro
        ═══════════════════════════════════════════════════════════════════ */}
        <Link
          to="/cockpit-pro"
          className="lg:hidden fixed flex items-center gap-2 px-4 py-3 rounded-full shadow-lg"
          style={{
            bottom: '85px',
            right: '16px',
            background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
            color: '#ffffff',
            boxShadow: '0 6px 20px rgba(34, 197, 94, 0.4)',
            fontWeight: 700,
            fontSize: '14px',
            zIndex: 10200,
          }}
          data-testid="floating-espace-pro-btn"
        >
          <Briefcase size={18} />
          Espace Pro
        </Link>

        {/* Mobile Bottom Navigation - Utilise la navbar globale standard */}
        {/* La navbar MobileBottomNav est maintenant gérée globalement dans App.js / MobileNavWrapper */}
        {/* Elle s'affiche automatiquement sur toutes les pages sauf celles exclues */}

        {/* Drawer "Plus" - Options supplémentaires */}
        <AnimatePresence>
          {showPlusDrawer && (
            <>
              {/* Overlay - ne couvre PAS la bottom nav pour ce drawer */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed top-0 left-0 right-0 bg-black/50 z-[10000] lg:hidden"
                style={{ bottom: 'calc(60px + env(safe-area-inset-bottom, 0px))' }}
                onClick={() => setShowPlusDrawer(false)}
              />
              
              {/* Drawer Bottom Sheet - s'arrête AU-DESSUS de la bottom nav, scrollable */}
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="fixed left-0 right-0 z-[10001] bg-white lg:hidden flex flex-col"
                style={{ 
                  borderRadius: '24px 24px 0 0',
                  boxShadow: '0 -8px 30px rgba(0,0,0,0.15)',
                  bottom: 'calc(60px + env(safe-area-inset-bottom, 0px))',
                  maxHeight: 'calc(100vh - 60px - env(safe-area-inset-bottom, 0px) - 100px)',
                }}
              >
                {/* Header avec Handle et bouton Fermer - FIXE */}
                <div className="flex items-center justify-between px-4 pt-3 pb-2 flex-shrink-0">
                  <div style={{ width: '44px' }} /> {/* Spacer pour centrer le handle */}
                  <div style={{ width: '36px', height: '4px', background: '#e0e0e0', borderRadius: '2px' }} />
                  <button
                    onClick={() => setShowPlusDrawer(false)}
                    className="p-2 hover:bg-gray-100 active:bg-gray-200 rounded-full transition-colors"
                    aria-label="Fermer"
                    style={{ minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <X size={22} className="text-gray-500" />
                  </button>
                </div>
                
                {/* Contenu scrollable */}
                <div className="overflow-y-auto flex-1 pb-4">
                <Link 
                  to="/cockpit-particulier/profil"
                  onClick={() => setShowPlusDrawer(false)}
                  className="flex items-center gap-4 mx-4 my-3 p-4 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-2xl active:bg-gray-100"
                >
                  {/* Photo de profil - BLEU */}
                  {user?.profile_photo ? (
                    <img 
                      src={user.profile_photo} 
                      alt="" 
                      className="w-14 h-14 rounded-full object-cover ring-2 ring-blue-200"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#2d4a6f] flex items-center justify-center ring-2 ring-blue-200">
                      <User size={28} className="text-white" />
                    </div>
                  )}
                  
                  {/* Texte */}
                  <div className="flex-1">
                    <p style={{ color: '#1a2a4a', fontSize: '18px', fontWeight: 700 }}>
                      Bonjour{user?.first_name ? `, ${user.first_name}` : ''}
                    </p>
                    <p style={{ color: '#1E3A5F', fontSize: '13px', fontWeight: 600 }}>
                      Voir le profil
                    </p>
                  </div>
                  
                  <ChevronRight size={20} className="text-gray-400" />
                </Link>
                
                {/* Liste des items */}
                <div>
                  {/* 2. Inviter mes proches - FOND ORANGE (action) */}
                  <button 
                    onClick={() => {
                      setShowPlusDrawer(false);
                      handleProtectedAction('inviter tes proches', () => window.location.href = '/cockpit-particulier/cercles');
                    }}
                    className="flex items-center gap-3.5 mx-4 mb-3 px-4 py-3.5 rounded-xl active:opacity-90 w-[calc(100%-2rem)] text-left"
                    style={{ 
                      background: 'linear-gradient(135deg, #F97316 0%, #E8650A 100%)',
                      boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)'
                    }}
                  >
                    <UserPlus size={20} color="#ffffff" style={{ width: '24px' }} />
                    <div className="flex-1">
                      <span style={{ color: '#ffffff', fontSize: '15px', fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", display: 'block' }}>
                        Inviter mes proches
                      </span>
                      <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>
                        Famille · Amis · Team
                      </span>
                    </div>
                    <ChevronRight size={18} color="rgba(255,255,255,0.7)" />
                  </button>
                  
                  {/* 3. Agenda */}
                  <button 
                    onClick={() => {
                      setShowPlusDrawer(false);
                      handleProtectedAction('ton agenda', () => window.location.href = '/cockpit-particulier/agenda');
                    }}
                    className="flex items-center gap-3.5 px-5 py-4 hover:bg-gray-50 active:bg-gray-100 w-full text-left"
                    style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
                  >
                    <Calendar size={20} className="text-[#1a2a4a]" style={{ width: '24px' }} />
                    <span style={{ color: '#1a2a4a', fontSize: '15px', fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", flex: 1 }}>
                      Agenda
                    </span>
                    <ChevronRight size={18} className="text-gray-300" />
                  </button>
                  
                  {/* 4. Notes */}
                  <button 
                    onClick={() => {
                      setShowPlusDrawer(false);
                      handleProtectedAction('tes notes', () => window.location.href = '/cockpit-particulier/notes');
                    }}
                    className="flex items-center gap-3.5 px-5 py-4 hover:bg-gray-50 active:bg-gray-100 w-full text-left"
                    style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
                  >
                    <FileText size={20} className="text-[#1a2a4a]" style={{ width: '24px' }} />
                    <span style={{ color: '#1a2a4a', fontSize: '15px', fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", flex: 1 }}>
                      Notes
                    </span>
                    <ChevronRight size={18} className="text-gray-300" />
                  </button>
                  
                  {/* 5. Boîte à idées */}
                  <button 
                    onClick={() => {
                      setShowPlusDrawer(false);
                      handleProtectedAction('ta boîte à idées', () => window.location.href = '/cockpit-particulier/boite-a-idees');
                    }}
                    className="flex items-center gap-3.5 px-5 py-4 hover:bg-gray-50 active:bg-gray-100 w-full text-left"
                    style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
                  >
                    <Lightbulb size={20} className="text-[#E8650A]" style={{ width: '24px' }} />
                    <span style={{ color: '#1a2a4a', fontSize: '15px', fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", flex: 1 }}>
                      Boîte à idées
                    </span>
                    <ChevronRight size={18} className="text-gray-300" />
                  </button>
                  
                  {/* 6. Documents */}
                  <button 
                    onClick={() => {
                      setShowPlusDrawer(false);
                      handleProtectedAction('tes documents', () => window.location.href = '/cockpit-particulier/documents');
                    }}
                    className="flex items-center gap-3.5 px-5 py-4 hover:bg-gray-50 active:bg-gray-100 w-full text-left"
                    style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
                  >
                    <FolderOpen size={20} className="text-[#1a2a4a]" style={{ width: '24px' }} />
                    <span style={{ color: '#1a2a4a', fontSize: '15px', fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", flex: 1 }}>
                      Documents
                    </span>
                    <ChevronRight size={18} className="text-gray-300" />
                  </button>
                  
                  {/* 7. Visioconférence */}
                  <button 
                    onClick={() => {
                      setShowPlusDrawer(false);
                      handleProtectedAction('la visioconférence', () => window.location.href = '/cockpit-particulier/visio');
                    }}
                    className="flex items-center gap-3.5 px-5 py-4 hover:bg-gray-50 active:bg-gray-100 w-full text-left"
                    style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
                  >
                    <Video size={20} className="text-[#1a2a4a]" style={{ width: '24px' }} />
                    <span style={{ color: '#1a2a4a', fontSize: '15px', fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", flex: 1 }}>
                      Visioconférence
                    </span>
                    <ChevronRight size={18} className="text-gray-300" />
                  </button>
                  
                  {/* Séparateur avant éléments "Bientôt" */}
                  <div style={{ height: '8px', background: '#f5f5f5' }} />
                  
                  {/* 8. Ma Liste de courses - Bientôt */}
                  <div 
                    className="flex items-center gap-3.5 px-5 py-4"
                    style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', opacity: 0.4, cursor: 'default' }}
                  >
                    <ShoppingCart size={20} className="text-gray-400" style={{ width: '24px' }} />
                    <span style={{ color: '#1a2a4a', fontSize: '15px', fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", flex: 1 }}>
                      Liste de courses
                    </span>
                    <span style={{
                      background: 'rgba(232,101,10,0.15)',
                      color: '#E8650A',
                      border: '1px solid rgba(232,101,10,0.3)',
                      borderRadius: '20px',
                      fontSize: '10px',
                      fontWeight: 700,
                      padding: '2px 8px',
                    }}>
                      Bientôt
                    </span>
                  </div>
                  
                  {/* 9. Partage de dépenses - Bientôt */}
                  <div 
                    className="flex items-center gap-3.5 px-5 py-4"
                    style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', opacity: 0.4, cursor: 'default' }}
                  >
                    <CreditCard size={20} className="text-gray-400" style={{ width: '24px' }} />
                    <span style={{ color: '#1a2a4a', fontSize: '15px', fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", flex: 1 }}>
                      Partage de dépenses
                    </span>
                    <span style={{
                      background: 'rgba(232,101,10,0.15)',
                      color: '#E8650A',
                      border: '1px solid rgba(232,101,10,0.3)',
                      borderRadius: '20px',
                      fontSize: '10px',
                      fontWeight: 700,
                      padding: '2px 8px',
                    }}>
                      Bientôt
                    </span>
                  </div>
                  
                  {/* 10. Coffre-fort - Bientôt */}
                  <div 
                    className="flex items-center gap-3.5 px-5 py-4"
                    style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', opacity: 0.4, cursor: 'default' }}
                  >
                    <Lock size={20} className="text-gray-400" style={{ width: '24px' }} />
                    <span style={{ color: '#1a2a4a', fontSize: '15px', fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", flex: 1 }}>
                      Coffre-fort
                    </span>
                    <span style={{
                      background: 'rgba(232,101,10,0.15)',
                      color: '#E8650A',
                      border: '1px solid rgba(232,101,10,0.3)',
                      borderRadius: '20px',
                      fontSize: '10px',
                      fontWeight: 700,
                      padding: '2px 8px',
                    }}>
                      Bientôt
                    </span>
                  </div>
                  
                  {/* Séparateur avant Espace Pro */}
                  <div style={{ height: '8px', background: '#f5f5f5' }} />
                  
                  {/* ESPACE PRO - Bouton pour accéder au Cockpit Pro */}
                  <Link 
                    to="/cockpit-pro"
                    onClick={() => setShowPlusDrawer(false)}
                    className="flex items-center gap-3.5 mx-4 my-3 p-4 rounded-2xl"
                    style={{ 
                      background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                      boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
                    }}
                  >
                    <Briefcase size={22} color="#ffffff" style={{ width: '24px' }} />
                    <div className="flex-1">
                      <span style={{ color: '#ffffff', fontSize: '15px', fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", display: 'block' }}>
                        Espace Pro
                      </span>
                      <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>
                        Découvre le Cockpit professionnel
                      </span>
                    </div>
                    <ChevronRight size={18} color="rgba(255,255,255,0.7)" />
                  </Link>
                  
                  {/* Séparateur avant Paramètres */}
                  <div style={{ height: '8px', background: '#f5f5f5' }} />
                  
                  {/* 11. Paramètres */}
                  <Link 
                    to="/cockpit-particulier/parametres"
                    onClick={() => setShowPlusDrawer(false)}
                    className="flex items-center gap-3.5 px-5 py-4 hover:bg-gray-50 active:bg-gray-100"
                  >
                    <Settings size={20} className="text-[#1a2a4a]" style={{ width: '24px' }} />
                    <span style={{ color: '#1a2a4a', fontSize: '15px', fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", flex: 1 }}>
                      Paramètres
                    </span>
                    <ChevronRight size={18} className="text-gray-300" />
                  </Link>
                </div>
                </div> {/* Fin du contenu scrollable */}
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Drawer Univers - Liste de tous les univers AbracadaBati */}
        <AnimatePresence>
          {showUniversDrawer && (
            <>
              {/* Overlay - z-index élevé pour couvrir le FAB */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 z-[10000] lg:hidden"
                onClick={() => setShowUniversDrawer(false)}
              />
              
              {/* Drawer Bottom Sheet - z-index supérieur au FAB */}
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="fixed bottom-0 left-0 right-0 z-[10001] bg-white rounded-t-3xl max-h-[85vh] overflow-hidden lg:hidden"
                style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
              >
                {/* Handle */}
                <div className="flex justify-center pt-3 pb-2">
                  <div className="w-10 h-1 bg-gray-300 rounded-full" />
                </div>
                
                {/* Header */}
                <div className="px-5 pb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Mes Univers</h2>
                    <p className="text-sm text-gray-500">Explore tous les services AbracadaBati</p>
                  </div>
                  <button 
                    onClick={() => setShowUniversDrawer(false)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <X size={24} className="text-gray-500" />
                  </button>
                </div>
                
                {/* Contenu scrollable */}
                <div className="overflow-y-auto max-h-[calc(85vh-100px)] px-4 pb-6">
                  {/* AbracadaBati - Univers Actif */}
                  <div className="mb-4">
                    <div className="flex items-center gap-3 px-3 py-3 bg-gradient-to-r from-orange-50 to-orange-100/50 rounded-xl border-2 border-orange-200 mb-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#E8650A] to-[#d45a09] rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/20">
                        <Building2 size={24} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900">AbracadaBati</h3>
                        <p className="text-xs text-gray-500">BTP, Artisans & Services</p>
                      </div>
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Actif</span>
                    </div>
                    
                    {/* Sous-sections AbracadaBati */}
                    <div className="grid grid-cols-2 gap-2 pl-2">
                      {Object.values(ABRACADABATI_CATEGORIES).map((category) => {
                        const CategoryIcon = category.icon;
                        return (
                          <Link
                            key={category.id}
                            to={category.subItems[0]?.path || '/cockpit-particulier'}
                            onClick={() => setShowUniversDrawer(false)}
                            className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl hover:border-orange-300 hover:bg-orange-50/50 transition-all active:scale-[0.98]"
                          >
                            <CategoryIcon size={20} className="text-[#E8650A]" />
                            <span className="text-sm font-medium text-gray-700">{category.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Bandeau "Bientôt disponibles" - Premium bleu marine */}
                  <div 
                    className="flex items-center justify-center gap-2 mb-4"
                    style={{
                      background: '#1a2a4a',
                      borderRadius: '12px',
                      padding: '10px 16px',
                    }}
                  >
                    <span style={{ fontSize: '14px' }}>✨</span>
                    <span style={{
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: 700,
                      letterSpacing: '1.5px',
                      textTransform: 'uppercase',
                    }}>
                      Bientôt disponibles
                    </span>
                    <span style={{ fontSize: '14px' }}>✨</span>
                  </div>
                  
                  {/* Grille des 19 univers Bientôt */}
                  <div className="grid grid-cols-2 gap-2">
                    {COMING_SOON_UNIVERS.map((univers) => (
                      <button
                        key={univers.id}
                        onClick={() => setSelectedUnivers(univers)}
                        className="flex flex-col items-center justify-center gap-1.5 p-3.5 transition-all active:scale-[0.97]"
                        style={{
                          background: 'rgba(26,42,74,0.06)',
                          border: '1px solid rgba(26,42,74,0.12)',
                          borderRadius: '14px',
                          textAlign: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        <span style={{ fontSize: '26px', opacity: 0.5 }}>{univers.emoji}</span>
                        <span style={{
                          fontSize: '13px',
                          fontWeight: 700,
                          color: '#1a2a4a',
                          opacity: 0.6,
                        }}>
                          {univers.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Popup Univers Bientôt - FIXED pour couvrir tout l'écran */}
                <AnimatePresence>
                  {selectedUnivers && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 flex items-center justify-center"
                      style={{
                        background: 'rgba(10,18,35,0.7)',
                        backdropFilter: 'blur(4px)',
                        zIndex: 10002,
                      }}
                      onClick={() => setSelectedUnivers(null)}
                    >
                      <motion.div
                        initial={{ scale: 0.9, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: 20 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          background: 'white',
                          borderRadius: '24px',
                          padding: '28px 24px',
                          width: '280px',
                          textAlign: 'center',
                          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                        }}
                      >
                        {/* Emoji */}
                        <div style={{ fontSize: '48px', marginBottom: '12px' }}>
                          {selectedUnivers.emoji}
                        </div>
                        
                        {/* Nom officiel */}
                        <h3 style={{
                          fontSize: '20px',
                          fontWeight: 800,
                          color: '#1a2a4a',
                          marginBottom: '4px',
                        }}>
                          {selectedUnivers.officialName}
                        </h3>
                        
                        {/* Description */}
                        <p style={{
                          fontSize: '14px',
                          color: '#888',
                          marginBottom: '16px',
                        }}>
                          {selectedUnivers.description}
                        </p>
                        
                        {/* Badge Bientôt */}
                        <div 
                          className="inline-flex items-center gap-1.5 mb-5"
                          style={{
                            background: 'rgba(232,101,10,0.1)',
                            border: '1.5px solid #E8650A',
                            color: '#E8650A',
                            padding: '8px 20px',
                            borderRadius: '20px',
                            fontSize: '13px',
                            fontWeight: 600,
                          }}
                        >
                          <span>✨</span>
                          <span>Bientôt disponible</span>
                        </div>
                        
                        {/* Bouton Fermer */}
                        <button
                          onClick={() => setSelectedUnivers(null)}
                          style={{
                            background: '#1a2a4a',
                            color: 'white',
                            borderRadius: '12px',
                            padding: '12px',
                            width: '100%',
                            fontSize: '14px',
                            fontWeight: 600,
                            border: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          Fermer
                        </button>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Page Content - avec padding pour header fixe, chat bar et nav bar mobile */}
        <main 
          className="flex-1 w-full max-w-full overflow-x-hidden" 
          style={{ 
            paddingTop: 'calc(92px + 0.75rem)',
            paddingBottom: 'calc(60px + 70px + env(safe-area-inset-bottom, 0px) + 16px)'
          }}
        >
          <Outlet />
        </main>

        {/* ═══ PANNEAU ARTIFACTS (Devis, Fichiers, Photos) ═══ */}
        <AnimatePresence>
          {showArtifactsPanel && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 md:left-[288px] bg-black/50 z-[10300]"
              onClick={() => setShowArtifactsPanel(false)}
            >
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header du panneau */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-[#1E3A5F] to-[#2d4a6f]">
                  <h2 className="text-lg font-bold text-white">Mes Documents</h2>
                  <button
                    onClick={() => setShowArtifactsPanel(false)}
                    className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                  >
                    <X size={20} className="text-white" />
                  </button>
                </div>

                {/* Contenu du panneau */}
                <div className="p-4 overflow-y-auto" style={{ height: 'calc(100% - 70px)' }}>
                  {/* Section Devis */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <FileText size={16} />
                      Mes Devis
                    </h3>
                    <div className="space-y-2">
                      <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-800">Devis #001 - Rénovation cuisine</p>
                          <p className="text-xs text-gray-500">12/04/2026 • En attente</p>
                        </div>
                        <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">En attente</span>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-800">Devis #002 - Plomberie SDB</p>
                          <p className="text-xs text-gray-500">08/04/2026 • Accepté</p>
                        </div>
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Accepté</span>
                      </div>
                    </div>
                  </div>

                  {/* Section Projets */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <FolderOpen size={16} />
                      Mes Projets
                    </h3>
                    <div className="space-y-2">
                      <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                        <p className="font-medium text-gray-800">Rénovation Appartement</p>
                        <p className="text-xs text-gray-500">3 devis • 2 artisans contactés</p>
                        <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                          <div className="bg-[#1E3A5F] h-1.5 rounded-full" style={{ width: '60%' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section Photos/Documents */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <Image size={16} />
                      Photos & Documents
                    </h3>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="aspect-square bg-gray-200 rounded-lg flex items-center justify-center">
                        <Image size={24} className="text-gray-400" />
                      </div>
                      <div className="aspect-square bg-gray-200 rounded-lg flex items-center justify-center">
                        <Image size={24} className="text-gray-400" />
                      </div>
                      <div className="aspect-square bg-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-2xl font-light">
                        +
                      </div>
                    </div>
                  </div>

                  {/* Section Conversations IA */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <Sparkles size={16} />
                      Historique Assistant
                    </h3>
                    <div className="space-y-2">
                      <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                        <p className="font-medium text-gray-800 text-sm">"Aide-moi à trouver un plombier"</p>
                        <p className="text-xs text-gray-500 mt-1">Aujourd'hui, 14:32</p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                        <p className="font-medium text-gray-800 text-sm">"Comparer les devis reçus"</p>
                        <p className="text-xs text-gray-500 mt-1">Hier, 10:15</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ═══ ARTIFACT VIEWER (Plein écran style Claude) ═══ */}
        <ArtifactViewer
          artifact={activeArtifact}
          isOpen={showArtifactViewer}
          onClose={() => {
            setShowArtifactViewer(false);
            setActiveArtifact(null);
          }}
          onAction={handleArtifactAction}
        />
        
        {/* Notifications Abra (mascotte) */}
        <AbraNotificationContainer />
      </div>
    </div>
  );
}
